import React from "react";
import { MailCheck, AlertTriangle } from "lucide-react";

/**
 * Amber, low-intensity hint we show next to every successful
 * email-capture success message. Reduces support volume from customers
 * reporting "I didn't receive the email" when it's actually in junk.
 *
 * Props:
 *   email     — the address we just sent to (rendered so user can confirm).
 *   what      — what the email contains, e.g. "your 10% discount code",
 *               "your sign-in link", "your order confirmation".
 *   domain    — fallback "from" display. Defaults to peptigenlabs.co.uk.
 *   size      — "sm" | "md" (default "md")
 */
export default function JunkFolderHint({ email, what = "the email", domain = "peptigenlabs.co.uk", size = "md" }) {
  const pad = size === "sm" ? "p-3" : "p-4";
  const text = size === "sm" ? "text-[11px]" : "text-[12px]";
  return (
    <div
      className={`${pad} rounded-sm border border-[#F7B500]/30 bg-[#F7B500]/10 text-[#F7B500] ${text} leading-relaxed`}
      data-testid="junk-folder-hint"
      role="status"
    >
      <div className="flex items-start gap-2">
        <MailCheck size={size === "sm" ? 12 : 14} className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[#F7FAFC] mb-0.5">
            <strong>Can't see {what}?</strong> Please check your <strong>Spam / Junk</strong> folder
            {email ? (
              <>
                {" "}
                at <span className="text-[#5EEAD4]" data-testid="junk-folder-hint-email">{email}</span>
              </>
            ) : null}
            .
          </div>
          <div className="text-[#9CA8B8]">
            Add <strong className="text-[#F7FAFC]">info@{domain}</strong> to your contacts so future
            emails land in your inbox, not your junk.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Slim inline variant — used in the middle of a longer dashboard.
 */
export function JunkFolderInline({ email }) {
  return (
    <div
      className="flex items-start gap-2 text-[11px] text-[#F7B500] leading-relaxed"
      data-testid="junk-folder-inline"
    >
      <AlertTriangle size={12} className="shrink-0 mt-0.5" />
      <span>
        Don't see the email{email ? <> at <span className="text-[#5EEAD4]">{email}</span></> : null}?
        Check your <strong>Spam / Junk</strong> folder and mark{" "}
        <strong className="text-[#F7FAFC]">info@peptigenlabs.co.uk</strong> as "Not junk" so future
        emails reach your inbox.
      </span>
    </div>
  );
}
